import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollText, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const REIN_FILTERS = [
  { key: "search", type: "search", label: "Search holder or credential", paths: ["holder_name", "credential_number"] },
  { key: "status", type: "select", label: "Status", path: "status",
    options: [
      { value: "submitted", label: "Submitted" }, { value: "under_review", label: "Under review" },
      { value: "approved", label: "Approved" }, { value: "denied", label: "Denied" },
    ] },
  { key: "date_from", type: "dateFrom", label: "Submitted from", path: "created_at" },
  { key: "date_to", type: "dateTo", label: "Submitted to", path: "created_at" },
];

export default function Reinstatement() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [creds, setCreds] = useState([]);
  const [cid, setCid] = useState("");
  const [reason, setReason] = useState("");
  const [remediation, setRemediation] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [r, c] = await Promise.all([api.get("/reinstatement-records"), api.get("/credentials")]);
    setItems(r.data);
    setCreds(c.data);
  };
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    if (!cid || !reason || !remediation) { toast.error("Fill all fields"); return; }
    setBusy(true);
    try {
      await api.post("/reinstatement-records", { credential_id: cid, reason, remediation_summary: remediation });
      toast.success("Reinstatement application submitted");
      setCid(""); setReason(""); setRemediation("");
      reload();
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };

  const decide = async (id, status) => {
    try {
      await api.patch(`/reinstatement-records/${id}`, { status });
      toast.success(`Reinstatement ${status}`);
      reload();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const isPractitioner = user.role === "practitioner" || user.role === "applicant";
  const eligible = creds.filter((c) => c.status === "revoked" || c.status === "suspended" || c.status === "expired");
  const filters = useFilters(items, REIN_FILTERS, { defaultSort: { field: "created_at", dir: "desc" } });
  const selection = useSelection(filters.filtered);
  const isAdmin = user.role === "admin";

  return (
    <div className="animate-fade-up" data-testid="reinstatement-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">IBTHM · Reinstatement</div>
        <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3"><ScrollText className="h-7 w-7" />Reinstatement applications</h1>
        <p className="text-sm text-slate-500 mt-1">Reactivate revoked, suspended or expired credentials after remediation.</p>
      </div>

      {isPractitioner && eligible.length > 0 && (
        <Card className="p-5 border-slate-200 bg-slate-50 mb-5">
          <h2 className="font-heading text-sm font-semibold text-primary mb-3">Submit a reinstatement application</h2>
          <div className="space-y-3">
            <div>
              <Label className="mb-1.5 block text-xs">Credential</Label>
              <Select value={cid} onValueChange={setCid}>
                <SelectTrigger data-testid="rein-cred"><SelectValue placeholder="Pick a credential" /></SelectTrigger>
                <SelectContent>
                  {eligible.map((c) => <SelectItem key={c.id} value={c.id}>{c.credential_number} · {c.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Reason for prior action</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rein-reason" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Remediation summary</Label>
              <Textarea rows={3} value={remediation} onChange={(e) => setRemediation(e.target.value)} data-testid="rein-remed" />
            </div>
            <Button onClick={submit} disabled={busy} className="bg-primary text-white hover:bg-[#113A64]" data-testid="rein-submit">{busy ? "Submitting…" : "Submit"}</Button>
          </div>
        </Card>
      )}

      <FilterPanel
        schemas={REIN_FILTERS} values={filters.values} set={filters.set} clear={filters.clear}
        count={filters.count} total={items.length} activeCount={filters.activeCount}
        sortFields={[
          { key: "created_at", label: "Submitted date" },
          { key: "holder_name", label: "Holder name" },
        ]}
        sort={filters.sort} setSort={filters.setSort} testid="rein-filters"
      />

      {isAdmin && <BulkActionsBar collection="reinstatement_records" selection={selection} onDone={reload} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && <th className="p-4 w-10"><Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="rein-select-all" /></th>}
                <th className="p-4">Credential</th>
                <th className="p-4">Holder</th>
                <th className="p-4">Reason</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filters.filtered.length === 0 && <tr><td colSpan={isAdmin ? 6 : 5} className="p-10 text-center text-slate-500">No reinstatement records match the current filters</td></tr>}
              {filters.filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  {isAdmin && <td className="p-4"><Checkbox checked={selection.isSelected(r.id)} onCheckedChange={() => selection.toggle(r.id)} data-testid={`rein-select-${r.id}`} /></td>}
                  <td className="p-4 font-mono-data text-xs text-primary">{r.credential_number}</td>
                  <td className="p-4 text-sm text-primary">{r.holder_name}</td>
                  <td className="p-4 text-xs text-slate-700 max-w-xs truncate">{r.reason}</td>
                  <td className="p-4"><Badge variant="outline" className="capitalize">{r.status.replace(/_/g, " ")}</Badge></td>
                  <td className="p-4 text-right">
                    {user.role === "admin" && r.status === "submitted" && (
                      <>
                        <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 mr-2" onClick={() => decide(r.id, "approved")} data-testid={`rein-approve-${r.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => decide(r.id, "denied")} data-testid={`rein-deny-${r.id}`}>
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Deny
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
